import {addLineItems, loadCart} from '@/cart/cart-store';

import {LineItemInput} from '@/cart/types';
import ProductSelectController from '@/components/buy-stack/ProductSelectController';
import ProductSelectControllerSignal from '@/product/product-select-controller';
import ProductSelectSection from '@/experiments/product-select-v3/ProductSelectSection';
import {ProductSelectSettings} from '@/settings/types';
import Settings from '@/components/buy-stack/ProductSelectSettings';
import {createDeliveryOptions} from '@/product/create-delivery-options';
import {endDrawer} from '../drawers';
import {getProduct} from '@/product/product-store';
import {
  getProductSellingPlans,
  setProductSellingPlans,
} from '@/selling-plan/selling-plan-store';
import {getSettings} from '@/settings/settings-store';
import placeholderProductSelectSettings from '@/settings/placeholder/product-select';
import register from 'preact-custom-element';
import {Product, ProductState} from '@/product/types';
import {optimisticCacheProduct} from '@/cart/optimistic-updates/optimistic-cache-product';
import {useEffect} from 'react';

register(ProductSelectView, 'product-select-view', [
  'productId',
  'productJson',
  'settingsHandle',
  'settingsJson',
]);

/**
 * The ProductSelectView is the entry point for the product selector.
 * It is responsible for initializing the controller and passing it to the ProductSelectSection.
 * It also removes the placeholder element from the DOM.
 * It gets the settings and passes them to the ProductSelectSection.
 */
export default function ProductSelectView({
  productId,
  productJson,
  settingsHandle,
  settingsJson,
}: ProductSelectViewProps) {
  const settingsFromLiquid = settingsJson
    ? (JSON.parse(settingsJson) as ProductSelectSettings)
    : null;
  const productFromLiquid = productJson
    ? (JSON.parse(productJson) as Product)
    : null;

  const plansFromLiquid = productFromLiquid?.rechargePlans;
  if (plansFromLiquid) {
    setProductSellingPlans(productId, plansFromLiquid);
  }
  const plans = plansFromLiquid ?? getProductSellingPlans(productId);

  const settings =
    getSettings<ProductSelectSettings>({
      handle: settingsHandle,
      type: 'product_select',
      followReferences: 1,
    }) ??
    settingsFromLiquid ??
    placeholderProductSelectSettings;

  const product = getProduct(parseInt(productId, 10)) ?? productFromLiquid;

  // Cache the product in the cart store
  // To enable optimistic 'add-to-cart' updates
  useEffect(() => {
    if (!product) return;
    optimisticCacheProduct(product as unknown as ProductState);
  }, [product]);

  const deliveryOptions = product
    ? createDeliveryOptions({
        product,
        sellingPlans: plans,
        settings,
      })
    : [];

  const cartStore = {
    loadCart: () => loadCart().then(({cart}) => cart),
    addLineItems: (items: LineItemInput[]) => {
      endDrawer.open();
      return addLineItems(items);
    },
  };

  const controller = new ProductSelectControllerSignal({
    cart: cartStore,
    deliveryOptions,
    product,
    productId: formatProductId(productId),
    products: {getProduct},
    settings,
  });

  if (product && settings) {
    clearPlaceHolders();
    return (
      <ProductSelectController.Provider value={controller}>
        <Settings.Provider value={settings}>
          <div className="px-7vw pb-8 pt-0 lg:pb-10 lg:pt-24">
            <ProductSelectSection />
          </div>
        </Settings.Provider>
      </ProductSelectController.Provider>
    );
  }
  return <></>;
}

/**
 * Removes the placeholder element from the DOM.
 * This allows us to show a loading skeleton while the product is loading.
 */
function clearPlaceHolders() {
  const placeholders = document.querySelectorAll(
    '[data-placeholder="product-select-view"]'
  );
  placeholders.forEach((placeholder) => {
    placeholder.remove();
  });
}

/**
 * Formats the product id to be a global id.
 */
function formatProductId(productId: string): string {
  return productId.startsWith('gid://')
    ? productId
    : `gid://shopify/Product/${productId}`;
}

export type ProductSelectViewProps = {
  productId: string;
  settingsHandle: string;
  productJson?: string;
  settingsJson?: string;
};
